<template>
  <Teleport to="body">
    <Transition name="actor-gate">
      <div
        v-if="modelValue"
        class="fixed inset-0 z-50 tw-flex items-center justify-center p-4"
        @click.self="close('backdrop')"
      >
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black/50 backdrop-blur-sm" @click="close('backdrop')"/>

        <!-- Modal -->
        <div class="actor-gate-card relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">

          <!-- Close -->
          <button
            class="absolute top-3 right-3 z-10 inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/60 hover:bg-white border-0 cursor-pointer transition-colors text-gray-500"
            :aria-label="$t('dismiss')"
            @click="close('close-button')"
          >
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>

          <!-- Decorative header -->
          <div class="actor-gate-header relative tw-flex items-center justify-center pt-10 pb-8">
            <div class="actor-gate-glow absolute inset-0 pointer-events-none"></div>
            <div class="actor-gate-icon relative tw-flex items-center justify-center w-16 h-16 rounded-2xl bg-white shadow-lg">
              <svg class="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
              </svg>
            </div>
          </div>

          <!-- Body -->
          <div class="px-6 sm:px-8 pb-7 text-center">
            <h3 class="text-lg font-bold text-gray-900 m-0 mt-2 mb-2">{{ $t('trp_actor_gate_title', { actor: actorLabel }) }}</h3>
            <p class="text-sm text-gray-500 leading-relaxed m-0 mb-6">{{ bodyText }}</p>

            <!-- Steps -->
            <div class="tw-flex flex-col gap-3 mb-7 text-left">
              <div
                v-for="(step, index) in steps"
                :key="index"
                class="tw-flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3"
              >
                <span class="tw-flex items-center justify-center w-6 h-6 rounded-full bg-primary text-white text-xs font-bold shrink-0">{{ index + 1 }}</span>
                <span class="text-sm font-medium text-gray-700">{{ step }}</span>
              </div>
            </div>

            <!-- Actions -->
            <button
              class="w-full py-3 rounded-xl bg-primary text-white text-sm font-bold border-0 cursor-pointer tw-flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
              @click="addActor"
            >
              {{ $t('add_actor_add_button', { actor: actorLabel }) }}
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
              </svg>
            </button>
            <button
              class="w-full mt-2 py-2.5 rounded-xl bg-transparent text-gray-400 hover:text-gray-600 text-sm font-medium border-0 cursor-pointer transition-colors"
              @click="close('maybe-later')"
            >
              {{ $t('trp_actor_gate_later') }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
  import TranslationsMixin from '@/util/TranslationsMixin'
  import { trackMatomoEvent } from '@/util/analytics.js'
  import { MATOMO_EVENT_ACTIONS, MATOMO_EVENT_CATEGORIES } from '@/constants/analytics-constants.js'

  export default {
    name: 'ActorRequiredModal',
    mixins: [TranslationsMixin],
    props: {
      modelValue: {
        type: Boolean,
        default: false,
      },
      // 'reports' = gate for Radar & Deep Dive Reports, 'network' = gate for adding contacts to the social map
      variant: {
        type: String,
        default: 'reports',
      },
    },
    emits: ['update:modelValue', 'add-actor'],
    computed: {
      bodyText () {
        return this.variant === 'network'
          ? this.$t('trp_actor_gate_text_network', { actor: this.actorLabel })
          : this.$t('trp_actor_gate_text', { actor: this.actorLabel })
      },
      steps () {
        return [
          this.$t('trp_actor_gate_step_1', { actor: this.actorLabel }),
          this.$t('trp_actor_gate_step_2'),
          this.variant === 'network'
            ? this.$t('trp_actor_gate_step_3_network')
            : this.$t('trp_actor_gate_step_3'),
        ]
      },
    },
    watch: {
      modelValue: {
        immediate: true,
        handler (visible) {
          if (visible) {
            this.track(MATOMO_EVENT_ACTIONS.MODAL_SHOWN)
          }
        },
      },
    },
    methods: {
      track (action, source = null) {
        const page = this.$route?.fullPath ?? window.location.pathname
        trackMatomoEvent(
          MATOMO_EVENT_CATEGORIES.ACTOR_REQUIRED_MODAL,
          action,
          source ? `${page} › ${source}` : page
        )
      },
      close (source = 'unknown') {
        this.track(MATOMO_EVENT_ACTIONS.DISMISS, source)
        this.$emit('update:modelValue', false)
      },
      addActor () {
        this.track(MATOMO_EVENT_ACTIONS.ADD_ACTOR)
        this.$emit('add-actor')
      },
    },
  }
</script>

<style lang="scss" scoped>
  .actor-gate-header {
    background: linear-gradient(160deg, var(--primary-extra-lightest, #eff6ff) 0%, var(--primary-lightest, #dbeafe) 100%);
  }

  .actor-gate-glow {
    background: radial-gradient(circle at 50% 0%, rgba(255, 255, 255, 0.7) 0%, transparent 65%);
  }

  .actor-gate-icon {
    animation: actor-gate-pop 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  }

  @keyframes actor-gate-pop {
    from {
      opacity: 0;
      transform: scale(0.6);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  /* Enter/leave transition */
  .actor-gate-enter-active,
  .actor-gate-leave-active {
    transition: opacity 0.2s ease;

    .actor-gate-card {
      transition: transform 0.25s cubic-bezier(0.34, 1.4, 0.64, 1), opacity 0.2s ease;
    }
  }

  .actor-gate-enter-from,
  .actor-gate-leave-to {
    opacity: 0;

    .actor-gate-card {
      transform: translateY(14px) scale(0.97);
      opacity: 0;
    }
  }
</style>
